// ─────────────────────────────────────────────────────────────────────────────
// Tournament Bench Plan — "who sits first, across these games"
//
// A pure checker for the coach-driven Bench Plan board in the Tournament view.
// The COACH sketches who starts on the bench in each game; this module does NOT
// decide for him. It validates his sketch and annotates each benched player with
// the "why" lenses he reasons in: likely 1st reliever, catcher depth, a similar-
// makeup teammate, and fairness (who has sat least / is overdue).
//
// No Firestore here. No mutation of inputs. Given roster + games + settings +
// the coach's current sketch, returns warnings + reason tags.
//
// Reuses existing engine concepts rather than reinventing them:
//   - normalizeBattingOrderStyle / batting-slot math (lineup-engine)
//   - getPitchingIntel (pitching-intel) for the 1st-reliever lens
//   - calculateOVR + per-position ratings + attribute buckets (utils) for similarity
// ─────────────────────────────────────────────────────────────────────────────

import { normalizeBattingOrderStyle } from './lineup-engine';
import { getPitchingIntel } from './pitching-intel';
import { PITCH_COUNT_RULESETS, buildCustomTournamentRules } from './pitch-count-rules';

export type BenchReasonKind =
  | 'reliever'
  | 'catcher'
  | 'similar'
  | 'sat-least'
  | 'overdue';

export interface BenchReason {
  kind: BenchReasonKind;
  /** Short label for the UI, e.g. "likely 1st reliever", "≈ similar to Jake R." */
  label: string;
}

export type WarningSeverity = 'red' | 'amber';

export interface BenchWarning {
  severity: WarningSeverity;
  message: string;
}

export interface BenchPlanGameResult {
  gameId: string;
  /** Target number of players who start benched for this game (roster − batting slots). */
  benchTarget: number;
  /** Players the coach has marked as starting on the bench, with their "why" tags. */
  benched: { playerId: string; reasons: BenchReason[] }[];
  warnings: BenchWarning[];
}

export interface BenchPlanResult {
  perGame: BenchPlanGameResult[];
  /** playerId → number of games this player starts benched across the set. */
  perPlayerTally: Record<string, number>;
  /** Fairness nudge across the whole set, or null if the spread is fine. */
  spreadWarning: BenchWarning | null;
}

/** The coach's working sketch: per game, which players start on the bench. */
export type BenchSketch = Record<string, { startingBenchIds: string[] }>;

interface MinimalGame {
  id: string;
  date?: string;
  opponent?: string;
  tournamentGameNumber?: number;
}

// ── Bench target ────────────────────────────────────────────────────────────
// How many players start benched for a game = present roster − batting slots.
//   '9 Players'  → 9 bat        (roster − 9 sit)
//   '10 Players' → 10 bat       (roster − 10 sit)
//   'Continuous' → everyone bats (0 sit — board still usable but target 0)
export function getBattingSlots(settings: any): number {
  const style = normalizeBattingOrderStyle(settings);
  if (style === '9 Players') return 9;
  if (style === '10 Players') return 10;
  return Infinity; // Continuous: everyone bats, no starting bench
}

export function getBenchTarget(rosterSize: number, settings: any): number {
  const slots = getBattingSlots(settings);
  if (!isFinite(slots)) return 0;
  return Math.max(0, rosterSize - slots);
}

// ── Position rating accessor (supports legacy `ratings` fallback) ─────────────
const posRating = (p: any, pos: string): number =>
  (p?.positions?.[pos] ?? p?.ratings?.[pos] ?? 0) as number;

const canCatch = (p: any): boolean => posRating(p, 'C') > 0;
const canPitch = (p: any): boolean =>
  posRating(p, 'P') > 0 ||
  ((p?.attributes?.pitching?.velocity || 0) + (p?.attributes?.pitching?.control || 0)) > 0;

// ── Similarity: are two players interchangeable enough to be a "fair swap"? ───
// Cheap, explainable check (NOT xOVR): close OVR, a shared position, and similar
// hitting attribute buckets. Returns the best similar teammate's name, or null.
function findSimilarTeammate(player: any, roster: any[]): any | null {
  const ovr = player.ovr ?? 0;
  const sharedPos = (other: any): boolean => {
    const posList = ['P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF'];
    return posList.some(pos => posRating(player, pos) > 0 && posRating(other, pos) > 0);
  };
  const attrDist = (a: any, b: any): number => {
    const ha = a?.attributes?.hitting || {};
    const hb = b?.attributes?.hitting || {};
    const keys = ['contact', 'power', 'eye', 'approach'];
    return keys.reduce((sum, k) => sum + Math.abs((ha[k] || 0) - (hb[k] || 0)), 0);
  };

  let best: any | null = null;
  let bestScore = Infinity;
  for (const other of roster) {
    if (other.id === player.id) continue;
    if (Math.abs((other.ovr ?? 0) - ovr) > 5) continue; // OVR within 5 pts
    if (!sharedPos(other)) continue;
    const dist = attrDist(player, other); // 0–16; lower = more alike
    if (dist > 4) continue; // hitting profiles must be close
    if (dist < bestScore) {
      bestScore = dist;
      best = other;
    }
  }
  return best;
}

// ── Season bench tally for fairness lens ─────────────────────────────────────
// Mirrors the lineup engine's fairness ordering: fewest season bench innings is
// "most due to play"; here we read it to explain a benched pick, not to decide.
const seasonBench = (p: any): number => p?.seasonBenchInnings ?? 0;

/**
 * Check the coach's bench sketch across a tournament's games.
 *
 * @param roster   full team roster (players)
 * @param games    the tournament's games, in display order
 * @param settings team.settings (drives batting slots / bench target)
 * @param sketch   coach's working sketch (per game: startingBenchIds)
 * @param ctx      tournament + pitching context for the reliever lens
 */
export function checkBenchPlan(
  roster: any[],
  games: MinimalGame[],
  settings: any,
  sketch: BenchSketch,
  ctx: {
    tournament: any;
    team: any;
    /** playerId → absent for a given gameId (kids who can't make that game). */
    absentByGame?: Record<string, string[]>;
  }
): BenchPlanResult {
  const byId = new Map(roster.map(p => [p.id, p]));
  const rosterSize = roster.length;
  const benchTarget = getBenchTarget(rosterSize, settings);

  // Pitching context (shared across games — tournament-level cap rules).
  const ruleset =
    PITCH_COUNT_RULESETS[
      ctx.tournament?.pitchCountRulesetOverride ||
        ctx.team?.settings?.tournamentOrg ||
        ctx.team?.settings?.pitchCountRuleset ||
        'little_league'
    ] || PITCH_COUNT_RULESETS.little_league;
  const ageGroup =
    ctx.team?.settings?.pitchCountAgeGroup || ctx.team?.ageGroup || '12U';
  const enforceGlobalRest = ctx.tournament?.enforceGlobalRest ?? false;
  const customRules = buildCustomTournamentRules(
    ruleset.id === 'custom',
    ctx.tournament,
    ctx.team?.settings
  );

  const isLikelyReliever = (p: any, gameDate: Date): boolean => {
    if (!canPitch(p)) return false;
    try {
      const intel = getPitchingIntel(
        p.pitchingLog || [],
        ruleset,
        ageGroup,
        gameDate,
        0,
        ctx.tournament?.id,
        enforceGlobalRest,
        customRules
      );
      return intel.isEligible; // an eligible arm sitting = resting/on-deck to pitch
    } catch {
      return false;
    }
  };

  const perPlayerTally: Record<string, number> = {};
  roster.forEach(p => (perPlayerTally[p.id] = 0));

  const perGame: BenchPlanGameResult[] = games.map(game => {
    const benchedIds = sketch[game.id]?.startingBenchIds ?? [];
    const absentIds = ctx.absentByGame?.[game.id] ?? [];
    const gameDate = game.date ? new Date(game.date) : new Date();
    // Present players = roster minus kids who can't make THIS game. The bench
    // target shrinks accordingly (fewer present → fewer need to sit).
    const presentCount = rosterSize - absentIds.filter(id => byId.has(id)).length;
    const gameBenchTarget = getBenchTarget(presentCount, settings);

    const benched = benchedIds
      .map(id => byId.get(id))
      .filter(Boolean)
      .map(p => {
        perPlayerTally[p!.id] = (perPlayerTally[p!.id] || 0) + 1;
        const reasons: BenchReason[] = [];

        if (isLikelyReliever(p, gameDate)) {
          reasons.push({ kind: 'reliever', label: 'likely 1st reliever' });
        }
        if (canCatch(p)) {
          reasons.push({ kind: 'catcher', label: 'catcher depth' });
        }
        const similar = findSimilarTeammate(p, roster);
        if (similar) {
          reasons.push({
            kind: 'similar',
            label: `≈ similar to ${similar.name || 'a teammate'}`,
          });
        }
        // Fairness lens: is this kid sitting least / most overdue across the team?
        const minSeasonBench = Math.min(...roster.map(seasonBench));
        if (seasonBench(p) <= minSeasonBench) {
          reasons.push({ kind: 'sat-least', label: 'has sat least' });
        }

        return { playerId: p!.id, reasons };
      });

    // ── Warnings for THIS game ───────────────────────────────────────────────
    const warnings: BenchWarning[] = [];

    // Count only present, benched players against the per-game target.
    const benchedPresent = benchedIds.filter(id => !absentIds.includes(id) && byId.has(id));
    if (gameBenchTarget > 0 && benchedPresent.length !== gameBenchTarget) {
      warnings.push({
        severity: 'red',
        message:
          benchedPresent.length > gameBenchTarget
            ? `${benchedPresent.length} on the bench, only ${gameBenchTarget} should sit (${presentCount} present − ${getBattingSlots(
                settings
              )} batting).`
            : `Only ${benchedPresent.length} benched, ${gameBenchTarget} should sit to start.`,
      });
    }

    // Can we still field a catcher? (Among players who are NOT benched/absent.)
    const onField = roster.filter(
      p => !benchedIds.includes(p.id) && !absentIds.includes(p.id)
    );
    if (!settings?.noCatcher && !onField.some(canCatch)) {
      warnings.push({
        severity: 'red',
        message: `No catcher available on the field this game.`,
      });
    }
    if (!settings?.noPitcher && !onField.some(canPitch)) {
      warnings.push({
        severity: 'red',
        message: `No pitcher available on the field this game.`,
      });
    }

    return { gameId: game.id, benchTarget: gameBenchTarget, benched, warnings };
  });

  // ── Fairness nudge across the whole set ────────────────────────────────────
  let spreadWarning: BenchWarning | null = null;
  const tallies = Object.values(perPlayerTally);
  if (tallies.length > 0 && benchTarget > 0) {
    const max = Math.max(...tallies);
    const min = Math.min(...tallies);
    if (max - min >= 2) {
      const mostId = Object.keys(perPlayerTally).find(
        id => perPlayerTally[id] === max
      );
      const leastId = Object.keys(perPlayerTally).find(
        id => perPlayerTally[id] === min
      );
      const mostName = mostId ? byId.get(mostId)?.name : undefined;
      const leastName = leastId ? byId.get(leastId)?.name : undefined;
      spreadWarning = {
        severity: 'amber',
        message:
          mostName && leastName
            ? `Lopsided rotation: ${mostName} sits ${max} game${
                max === 1 ? '' : 's'
              }, ${leastName} sits ${min}. Spread the bench more evenly.`
            : `Lopsided rotation: some players sit ${max} games while others sit ${min}.`,
      };
    }
  }

  return { perGame, perPlayerTally, spreadWarning };
}

// ═════════════════════════════════════════════════════════════════════════════
// Round Robin pitching — Stage 1: starting pitcher + relief plan
//
// Reuses getPitchingIntel for each arm's weekend availability, and adds a
// cross-game check the per-game planner can't see: same arm double-booked across
// the round robin, or chosen while capped / on required rest.
// ═════════════════════════════════════════════════════════════════════════════

export type ArmStatus = 'ready' | 'resting' | 'capped';

export interface PitcherAvailability {
  playerId: string;
  status: ArmStatus;
  /** Short label, e.g. "Ready", "Resting", "Tournament capped". */
  label: string;
  /** Pitches remaining (daily or tournament-scoped per the intel). */
  pitchesRemaining: number;
  isEligible: boolean;
}

/** The coach's round-robin pitching sketch, per game. */
export interface RoundRobinGamePlan {
  startingPitcherId?: string;
  reliefIds?: string[];        // ordered: first reliever first
  benchReliefIds?: string[];   // relievers to bench-to-start (fold into startingBenchIds)
  startingBenchIds?: string[]; // who sits to start (Stage 0 / supporting input)
}
export type RoundRobinPlan = Record<string, RoundRobinGamePlan>;

interface PitchingCtx {
  tournament: any;
  team: any;
}

function resolvePitchingConfig(ctx: PitchingCtx) {
  const ruleset =
    PITCH_COUNT_RULESETS[
      ctx.tournament?.pitchCountRulesetOverride ||
        ctx.team?.settings?.tournamentOrg ||
        ctx.team?.settings?.pitchCountRuleset ||
        'little_league'
    ] || PITCH_COUNT_RULESETS.little_league;
  const ageGroup = ctx.team?.settings?.pitchCountAgeGroup || ctx.team?.ageGroup || '12U';
  const enforceGlobalRest = ctx.tournament?.enforceGlobalRest ?? false;
  const customRules = buildCustomTournamentRules(ruleset.id === 'custom', ctx.tournament, ctx.team?.settings);
  return { ruleset, ageGroup, enforceGlobalRest, customRules };
}

/** Availability for one arm on a given game date, from the existing intel engine. */
export function getPitcherAvailability(
  player: any,
  gameDate: Date,
  ctx: PitchingCtx
): PitcherAvailability {
  const { ruleset, ageGroup, enforceGlobalRest, customRules } = resolvePitchingConfig(ctx);
  try {
    const intel = getPitchingIntel(
      player.pitchingLog || [],
      ruleset,
      ageGroup,
      gameDate,
      0,
      ctx.tournament?.id,
      enforceGlobalRest,
      customRules
    );
    const capped = intel.eventRemaining != null && intel.eventRemaining <= 0;
    const status: ArmStatus = !intel.isEligible ? 'resting' : capped ? 'capped' : 'ready';
    return {
      playerId: player.id,
      status,
      label: status === 'ready' ? 'Ready' : status === 'resting' ? 'Resting' : 'Tournament capped',
      pitchesRemaining: intel.eventRemaining ?? intel.dailyRemaining ?? intel.pitchesRemaining,
      isEligible: intel.isEligible && !capped,
    };
  } catch {
    return { playerId: player.id, status: 'ready', label: 'Ready', pitchesRemaining: 0, isEligible: true };
  }
}

export interface RoundRobinPitchingResult {
  /** gameId → warnings about that game's pitching picks. */
  warningsByGame: Record<string, BenchWarning[]>;
  /** gameId → playerId → availability, for the arms the coach is choosing among. */
  availabilityByGame: Record<string, Record<string, PitcherAvailability>>;
  /** playerId → how many games this arm is set to START across the round robin. */
  startCountByPitcher: Record<string, number>;
}

/**
 * Validate the round-robin pitching sketch across all games.
 * Catches what the single-game planner can't: an arm double-booked to start, or
 * a starter/reliever who is capped or on required rest for that game.
 */
export function checkRoundRobinPitching(
  roster: any[],
  games: MinimalGame[],
  plan: RoundRobinPlan,
  ctx: PitchingCtx
): RoundRobinPitchingResult {
  const byId = new Map(roster.map(p => [p.id, p]));
  const warningsByGame: Record<string, BenchWarning[]> = {};
  const availabilityByGame: Record<string, Record<string, PitcherAvailability>> = {};
  const startCountByPitcher: Record<string, number> = {};

  // Count how many games each arm is set to start (for double-book detection).
  games.forEach(g => {
    const sp = plan[g.id]?.startingPitcherId;
    if (sp) startCountByPitcher[sp] = (startCountByPitcher[sp] || 0) + 1;
  });

  games.forEach(game => {
    const gp = plan[game.id] || {};
    const gameDate = game.date ? new Date(game.date) : new Date();
    const warnings: BenchWarning[] = [];
    const avail: Record<string, PitcherAvailability> = {};

    const considered = new Set<string>([
      ...(gp.startingPitcherId ? [gp.startingPitcherId] : []),
      ...(gp.reliefIds || []),
    ]);
    considered.forEach(pid => {
      const p = byId.get(pid);
      if (p) avail[pid] = getPitcherAvailability(p, gameDate, ctx);
    });

    // Starter checks
    if (gp.startingPitcherId) {
      const a = avail[gp.startingPitcherId];
      const name = byId.get(gp.startingPitcherId)?.name || 'Starter';
      if (a && a.status === 'resting') {
        warnings.push({ severity: 'red', message: `${name} is on required rest this game. Pick another starter.` });
      } else if (a && a.status === 'capped') {
        warnings.push({ severity: 'red', message: `${name} is at the tournament pitch cap. Pick another starter.` });
      }
      if ((startCountByPitcher[gp.startingPitcherId] || 0) > 1) {
        warnings.push({ severity: 'amber', message: `${name} is set to start more than one game. Check rest between outings.` });
      }
    } else {
      warnings.push({ severity: 'amber', message: `No starting pitcher set for this game.` });
    }

    // Relief checks
    (gp.reliefIds || []).forEach(rid => {
      const a = avail[rid];
      const name = byId.get(rid)?.name || 'Reliever';
      if (a && a.status === 'resting') {
        warnings.push({ severity: 'red', message: `${name} (relief) is on required rest this game.` });
      } else if (a && a.status === 'capped') {
        warnings.push({ severity: 'red', message: `${name} (relief) is at the tournament pitch cap.` });
      }
      if (rid === gp.startingPitcherId) {
        warnings.push({ severity: 'red', message: `${name} can't be both the starter and the reliever.` });
      }
    });

    warningsByGame[game.id] = warnings;
    availabilityByGame[game.id] = avail;
  });

  return { warningsByGame, availabilityByGame, startCountByPitcher };
}
